import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import { Loader2 } from 'lucide-react';

import { authenticationAtom, jwtTokensAtom } from './State';
import { jwtAuthService } from './JWTAuthService';
import { configuration } from './Configuration';
import { BRAND } from "@/brand/brand";
import { useCustomerLogo } from '@/hooks/useCustomerLogo';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

/**
 * MagicLinkPage handles authentication via magic link tokens
 * Used for seamless authentication after registration
 */
export default function MagicLinkPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [, setAuthentication] = useAtom(authenticationAtom);
  const [, setJwtTokens] = useAtom(jwtTokensAtom);
  const [error, setError] = useState<string | null>(null);
  const [validating, setValidating] = useState(true);
  const { logo, isFallback } = useCustomerLogo();

  useEffect(() => {
    const validateMagicLink = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setError('Kein Token gefunden. Bitte versuche dich erneut anzumelden.');
        setValidating(false);
        return;
      }

      try {
        console.log('Validating magic link...');

        const response = await fetch(`${configuration.serverUrl}/auth/magic-link/validate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));

          if (response.status === 401) {
            setError('Der Link ist abgelaufen oder ungültig. Bitte melde dich erneut an.');
          } else {
            setError(errorData.error || 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
          }
          setValidating(false);
          return;
        }

        const result = await response.json();
        // never log the answer: it holds the access and refresh token

        // Store JWT tokens
        if (result.accessToken && result.refreshToken) {
          jwtAuthService.setTokens({
            accessToken: result.accessToken,
            refreshToken: result.refreshToken,
          });

          setJwtTokens({
            accessToken: result.accessToken,
            refreshToken: result.refreshToken,
          });
        }

        // Set authentication state
        if (result.user) {
          setAuthentication(result.user);
        }

        console.log('Authentication successful, redirecting to chatbot...');

        // Redirect to chatbot
        navigate('/chatbot', { replace: true });
      } catch (error) {
        console.error('Error validating magic link:', error instanceof Error ? error.message : 'unknown error');
        setError('Ein Netzwerkfehler ist aufgetreten. Bitte versuche es erneut.');
        setValidating(false);
      }
    };

    validateMagicLink();
  }, [searchParams, navigate, setAuthentication, setJwtTokens]);

  return (
    <div className="flex min-h-screen w-full flex-col items-center justify-center gap-6 bg-background p-4">
      <img
        src={logo}
        alt={isFallback ? BRAND.name : ''}
        className="h-16 w-auto max-w-[240px] object-contain"
      />
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col items-center gap-4 p-6 text-center">
          {validating ? (
            <>
              <Loader2
                aria-hidden="true"
                className="h-8 w-8 animate-spin text-muted-foreground"
              />
              <div role="status" className="flex flex-col gap-1">
                <h1 className="text-base font-semibold">Authentifizierung läuft...</h1>
                <p className="text-sm text-muted-foreground">Du wirst gleich weitergeleitet.</p>
              </div>
            </>
          ) : error ? (
            <>
              <Alert variant="destructive" className="text-left">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              <Button onClick={() => navigate('/login')} className="w-full sm:w-auto">
                Zur Anmeldung
              </Button>
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
